import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_OG_LOCALES } from 'i18n/language';
import useLanguage from 'hooks/useLanguage';
import { useStore } from 'context/StoreContext';
import { canonicalUrl, pageKey } from './canonical';
import { snapshotHeadIsCurrent } from './snapshotHandoff';

const DESCRIPTION_MAX = 155;
// Same robots values as the server's snapshot heads (docs/PHASE4.md section 3).
export const ROBOTS_DEFAULT = 'max-image-preview:large';
export const ROBOTS_HIDDEN = 'noindex,follow';

/**
 * A plain text (a shop tagline, a translated sentence) as a meta description:
 * first paragraph, shortened at a word boundary like the server's snapshots.
 * Catalog pages do not use it: their description is the API's `seo.description`.
 */
export function shortDescription(text, max = DESCRIPTION_MAX) {
  const t = String(text || '')
    .split(/\n\s*\n/)[0]
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?\-–—]+$/, '')}…`;
}

/** The tag `selector` finds, created as `<tag attr=key>` when missing; null value removes it. */
function upsert(tag, attr, key, value, valueAttr = 'content') {
  const selector = `${tag}[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (value === null || value === undefined || value === '') {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(valueAttr, value);
}

const ISO = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
};

/**
 * Keep the <head> in step with client-side navigation, with the tags the
 * server's snapshot of the same page has: title, description, canonical,
 * robots, og:type (and article:*_time on articles) and the share titles.
 * Tags only the snapshot can get right (JSON-LD, the 1200x630 share image)
 * are removed rather than left pointing at the previous page.
 *
 *   noindex   a private page (order, payment, cart): no share tags, no
 *             canonical, no URL of it in the head (it may hold a token), and
 *             robots "noindex, nofollow"
 *   hidden    a public page the shop hid from Google (seo noindex): robots
 *             "noindex,follow", share tags kept (it can still be shared)
 */
export function applyPageMeta({
  title,
  shareTitle = title,
  description,
  url,
  language,
  noindex = false,
  hidden = false,
  siteName,
  ogType = 'website',
  publishedTime,
  modifiedTime,
  prevUrl,
  nextUrl,
}) {
  document.title = title;
  if (noindex) {
    // A private page (an order, a payment): no share tags, no canonical, no
    // URL of it anywhere in the head (the URL holds the order's access token),
    // and an explicit noindex next to the X-Robots-Tag header.
    document.head
      .querySelectorAll(
        'script[type="application/ld+json"], meta[property^="og:"], meta[property^="article:"], meta[name^="twitter:"], link[rel="canonical"], link[rel="prev"], link[rel="next"], meta[name="robots"], meta[name="description"]'
      )
      .forEach((el) => el.remove());
    upsert('meta', 'name', 'robots', 'noindex, nofollow');
    return;
  }
  upsert('meta', 'name', 'description', description || null);
  upsert('meta', 'name', 'robots', hidden ? ROBOTS_HIDDEN : ROBOTS_DEFAULT);
  upsert('link', 'rel', 'canonical', url, 'href');
  upsert('meta', 'property', 'og:title', shareTitle);
  upsert('meta', 'property', 'og:description', description || null);
  upsert('meta', 'property', 'og:url', url);
  upsert('meta', 'property', 'og:type', ogType === 'article' ? 'article' : 'website');
  if (siteName) upsert('meta', 'property', 'og:site_name', siteName);
  if (language) upsert('meta', 'property', 'og:locale', LANGUAGE_OG_LOCALES[language] || LANGUAGE_OG_LOCALES.id);
  upsert('meta', 'name', 'twitter:title', shareTitle);
  upsert('meta', 'name', 'twitter:description', description || null);
  const article = ogType === 'article';
  upsert('meta', 'property', 'article:published_time', article ? ISO(publishedTime) : null);
  upsert('meta', 'property', 'article:modified_time', article ? ISO(modifiedTime) : null);
  document.head
    .querySelectorAll(
      'script[type="application/ld+json"], meta[property^="og:image"], meta[name^="twitter:image"]'
    )
    .forEach((el) => el.remove());
  upsert('link', 'rel', 'prev', prevUrl || null, 'href');
  upsert('link', 'rel', 'next', nextUrl || null, 'href');
}

/**
 * Sets document.title to "<page> | <store name>" (and the description,
 * canonical, robots and share tags) on client-side navigation, so screen
 * reader users and browser history get a meaningful title and the head says
 * what the snapshot of the same page says. On the first page of a visit served
 * by the SEO Edge Function the head is already right (and is what crawlers and
 * link previews read), so it is left alone until the visitor navigates (also
 * to another ?halaman= of a listing) or reads the site in another language
 * than the (Indonesian) snapshot.
 *
 *   usePageTitle(product ? product.name : null, { seo: product?.seo })
 *
 * Options:
 *   seo                    the API's `seo` of a product, category, info page or
 *                          article ({ title, description, noindex }): the shop's
 *                          SEO title (else the page name), the final meta
 *                          description exactly as the snapshot has it, and
 *                          robots "noindex,follow". Used as it is: the server
 *                          is the one place that composes them
 *   description            meta and share description of a page without `seo`
 *                          (home tagline, listings, translated texts); shortened
 *                          like the server does
 *   noindex                private page (cart, checkout, payment, orders): see applyPageMeta
 *   page, pageCount        page number (and number of pages) of a paginated
 *                          listing: " – Halaman N" in the title, ?halaman=N in
 *                          the canonical, rel=prev/next links
 *   ogType                 'article' for articles (with publishedTime / modifiedTime)
 */
export default function usePageTitle(
  title,
  {
    seo = null,
    description,
    noindex = false,
    page = 1,
    pageCount = 0,
    ogType = 'website',
    publishedTime,
    modifiedTime,
  } = {}
) {
  const { t } = useTranslation();
  const { identity } = useStore();
  const { pathname, search } = useLocation();
  const { language } = useLanguage();
  // The API's `seo` wins over what the page passes: the snapshot has the same.
  const name = (seo && seo.title) || title;
  const text = seo ? seo.description : shortDescription(description);
  const hidden = Boolean(seo && seo.noindex);
  const pageSuffix = page >= 2 ? ` – ${t('pagination.titleSuffix', { page })}` : '';
  const key = pageKey(pathname, search);
  // Re-runs when the language changes: titles the app builds ("Keranjang" /
  // "Cart") arrive here already translated, and og:locale follows.
  useEffect(() => {
    // Always asked (it forgets the snapshot at the first navigation), even for a private page.
    const snapshotIsCurrent = snapshotHeadIsCurrent(key, language);
    if (snapshotIsCurrent && !noindex) return;
    applyPageMeta({
      title: name ? `${name}${pageSuffix} | ${identity.name}` : identity.name,
      // og:title / twitter:title: the page's own title (the app shows og:site_name next to it).
      shareTitle: name ? `${name}${pageSuffix}` : identity.name,
      description: text,
      url: canonicalUrl(pathname, page),
      language,
      noindex,
      hidden,
      siteName: identity.name,
      ogType,
      publishedTime,
      modifiedTime,
      prevUrl: page >= 2 ? canonicalUrl(pathname, page - 1) : null,
      nextUrl: page < pageCount ? canonicalUrl(pathname, page + 1) : null,
    });
  }, [
    name,
    text,
    identity.name,
    pathname,
    key,
    page,
    pageCount,
    pageSuffix,
    language,
    noindex,
    hidden,
    ogType,
    publishedTime,
    modifiedTime,
  ]);
}
